import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@pandiafi/design-system'
import { Modal, ContextNotification } from '@pandiafi/components'
import { DateTime } from 'luxon'

import { useAPICancelReservation } from '../../api/apiHooks/useAPICancelReservation'
import { useAPITerminateRecurringReservation } from '../../api/apiHooks/useAPITerminateRecurringReservation'
import type { TimeSlotForResident } from '../../../common/validations/reservationCalendarSchemas'
import {
  formatDate,
  formatReservationDateTime,
  formatTimeRange,
  getWeekdayTranslationKey,
} from '../../utils/dateUtils'
import { ReservationActionSuccess } from './ReservationActionSuccess'
import { ResidentAppError } from '../../../common/error'
import { ResidentAppErrorCode } from '../../../common/types'
import type { ReservationCalendarForResident } from './types'
import { useAPICancelRecurringReservation } from '../../api/apiHooks/useAPICancelRecurringReservation'

import { useAPIGetReservationsSuspense } from 'api/apiHooks/useAPIGetReservations'
import { useAPIGetContractSuspense } from 'api/apiHooks/useAPIGetContract'

interface CancelReservationModalProps {
  show: boolean
  onClose: () => void
  calendar: ReservationCalendarForResident | null
  timeSlot: TimeSlotForResident | null
  contractUuid: string
  readonly?: boolean
}

export const CancelReservationModal: React.FC<CancelReservationModalProps> = ({
  show,
  onClose,
  calendar,
  timeSlot,
  contractUuid,
  readonly = false,
}) => {
  const { t } = useTranslation('reservations')
  const [showSuccess, setShowSuccess] = useState(false)
  const [successAction, setSuccessAction] = useState<'cancel' | 'terminate'>(
    'cancel'
  )
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const cancelReservationMutation = useAPICancelReservation(contractUuid)
  const cancelRecurringReservationMutation =
    useAPICancelRecurringReservation(contractUuid)
  const terminateReservationMutation =
    useAPITerminateRecurringReservation(contractUuid)
  const { data: allReservations = [] } =
    useAPIGetReservationsSuspense(contractUuid)
  const { data: contract } = useAPIGetContractSuspense(contractUuid)
  const cancelPending =
    cancelReservationMutation.isPending ||
    cancelRecurringReservationMutation.isPending

  useEffect(() => {
    if (show) {
      setErrorMsg(null)
      setShowSuccess(false)
    }
  }, [show, timeSlot])

  if (!calendar || !timeSlot) {
    return null
  }

  const ownReservation = timeSlot.reservations[0] || null
  if (!ownReservation) {
    return null
  }

  const singleReservationId = ownReservation.id
  const { reservationRecurrenceId } = ownReservation
  const isRecurring = reservationRecurrenceId !== null

  const reservationDetails = allReservations.find(
    (r) =>
      r.id === singleReservationId ||
      (reservationRecurrenceId !== null &&
        r.reservationRecurrenceId === reservationRecurrenceId)
  )
  if (!reservationDetails) {
    return null
  }

  const slotDate = DateTime.fromISO(timeSlot.startsAt).toISODate()
  if (!slotDate) {
    return null
  }

  const todayISO = DateTime.now().toISODate()
  const isSameDay = todayISO === slotDate
  const recurrenceStartDatePassed =
    DateTime.fromISO(reservationDetails.startsAt).toISODate()! <= todayISO
  const shouldTerminate = isRecurring && recurrenceStartDatePassed

  const resolveErrorMessage = (err: unknown) => {
    if (err instanceof ResidentAppError) {
      switch (err.errorCode) {
        case ResidentAppErrorCode.RECURRING_RESERVATION_ALREADY_STARTED:
          return t('cancelModal.recurringCancelNotAllowed')
        case ResidentAppErrorCode.RECURRING_RESERVATION_TERMINATION_TODAY_NOT_ALLOWED:
          return t('cancelModal.terminateSameDayNotAllowed')
        default:
          break
      }
    }

    return t('cancelModal.cancelFailed')
  }

  const handleCancel = () => {
    setErrorMsg(null)

    if (reservationRecurrenceId) {
      cancelRecurringReservationMutation.mutate(
        {
          reservationRecurrenceId,
        },
        {
          onSuccess: () => {
            setSuccessAction('cancel')
            setShowSuccess(true)
          },
          onError: (err) => {
            setErrorMsg(resolveErrorMessage(err))
          },
        }
      )
    } else {
      cancelReservationMutation.mutate(
        {
          reservationId: singleReservationId,
        },
        {
          onSuccess: () => {
            setSuccessAction('cancel')
            setShowSuccess(true)
          },
          onError: (err) => {
            setErrorMsg(resolveErrorMessage(err))
          },
        }
      )
    }
  }

  const handleTerminate = () => {
    setErrorMsg(null)
    terminateReservationMutation.mutate(
      {
        reservationRecurrenceId: reservationRecurrenceId!,
        terminateFromDate: slotDate,
      },
      {
        onSuccess: () => {
          setSuccessAction('terminate')
          setShowSuccess(true)
        },
        onError: (err) => {
          setErrorMsg(resolveErrorMessage(err))
        },
      }
    )
  }

  const handleClose = () => {
    setShowSuccess(false)
    setErrorMsg(null)
    onClose()
  }

  const reservationTimeRange = formatTimeRange(
    reservationDetails.startsAt,
    reservationDetails.endsAt
  )
  const weekdayTranslated = t(
    getWeekdayTranslationKey(reservationDetails.startsAt)
  )
  const reservationStartDateFormatted = formatDate(reservationDetails.startsAt)
  const slotDateFormatted = formatDate(timeSlot.startsAt)
  const modalKey = `${timeSlot.id}-${timeSlot.startsAt}`
  const modalRenderKey = `${modalKey}-${showSuccess ? 'success' : 'form'}`

  return (
    <Modal
      show={show}
      onClose={showSuccess ? handleClose : onClose}
      title={t('cancelModal.title')}
      key={modalRenderKey}
      headerElement={
        showSuccess ? (
          <div>
            <div className="border border-gray-700 rounded-lg overflow-hidden">
              <ContextNotification type="success" textAlign="center">
                {successAction === 'terminate'
                  ? t('timeFormatting.recurringTerminated')
                  : isRecurring
                    ? t('timeFormatting.recurringCancelled')
                    : t('timeFormatting.singleCancelled')}
              </ContextNotification>
            </div>
          </div>
        ) : undefined
      }
    >
      {showSuccess ? (
        <ReservationActionSuccess
          calendar={calendar}
          isRecurring={isRecurring}
          action={successAction}
          onClose={handleClose}
          reservation={{
            startsAt: reservationDetails.startsAt,
            endsAt: reservationDetails.endsAt,
          }}
        />
      ) : (
        <>
          <Modal.Notifications>
            {contract.status === 'terminated' ? (
              <ContextNotification type="info">
                {t('cancelModal.contractTerminated')}
              </ContextNotification>
            ) : isSameDay ? (
              <ContextNotification type="info">
                {t('cancelModal.sameDayNotAllowed')}
              </ContextNotification>
            ) : shouldTerminate ? (
              <ContextNotification type="info">
                {t('cancelModal.terminateWarning')}
              </ContextNotification>
            ) : (
              <ContextNotification type="info">
                {t('cancelModal.recurringWarning')}
              </ContextNotification>
            )}
            {errorMsg ? (
              <ContextNotification type="error">{errorMsg}</ContextNotification>
            ) : null}
          </Modal.Notifications>
          <Modal.Content>
            <div className="space-y-4">
              <div>
                <div className="font-medium text-gray-900">{calendar.name}</div>
                {isRecurring && (
                  <>
                    <div className="text-gray-600">
                      {t('cancelModal.recurringInfo', {
                        weekday: weekdayTranslated,
                        timeRange: reservationTimeRange,
                        date: reservationStartDateFormatted,
                      })}
                    </div>
                    <div className="text-gray-600">
                      {t('cancelModal.effectiveFrom', {
                        date: slotDateFormatted,
                      })}
                    </div>
                  </>
                )}
                {!isRecurring && (
                  <div className="text-gray-600">
                    {formatReservationDateTime(
                      reservationDetails.startsAt,
                      reservationDetails.endsAt
                    )}
                  </div>
                )}
              </div>
            </div>
          </Modal.Content>

          <Modal.Buttons>
            <Button variant="cancel" onClick={onClose}>
              {t('cancelModal.cancel')}
            </Button>
            {isSameDay ? (
              <Button
                variant="primary"
                disabled
                className="cursor-not-allowed opacity-50"
              >
                {t('cancelModal.notAllowed')}
              </Button>
            ) : shouldTerminate ? (
              <Button
                variant="primary"
                onClick={handleTerminate}
                disabled={
                  terminateReservationMutation.isPending ||
                  contract.status === 'terminated' ||
                  readonly
                }
                loading={terminateReservationMutation.isPending}
              >
                {t('cancelModal.confirmTerminate')}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleCancel}
                disabled={
                  cancelPending || contract.status === 'terminated' || readonly
                }
                loading={cancelPending}
              >
                {t('cancelModal.confirmRecurring')}
              </Button>
            )}
          </Modal.Buttons>
        </>
      )}
    </Modal>
  )
}
